import type { Router, LocationQueryValue } from 'vue-router'
import { sanitizeInput, validateInput } from '../utils/security'

export function setupSecurityMiddleware(router: Router) {
  // Protection contre les attaques XSS
  router.beforeEach((to, from, next) => {
    // Sanitize query parameters
    if (to.query) {
      const sanitizedQuery = Object.keys(to.query).reduce((acc: Record<string, LocationQueryValue | LocationQueryValue[]>, key: string) => {
        const value = to.query[key]
        if (typeof value === 'string') {
          acc[key] = sanitizeInput(value)
        } else if (Array.isArray(value)) {
          acc[key] = value.map(v => typeof v === 'string' ? sanitizeInput(v) : v)
        } else {
          acc[key] = value
        }
        return acc
      }, {})
      
      to.query = sanitizedQuery
    }

    // Validate route parameters
    if (to.params) {
      const isValid = Object.values(to.params).every(param => 
        typeof param === 'string' && validateInput(param as string)
      )
      
      if (!isValid) {
        next({ name: 'not-found' })
        return
      }
    }

    next()
  })

  // Protection contre le clickjacking
  if (window.top !== window.self) {
    throw new Error('Cette application ne peut pas être chargée dans un iframe')
  }

  // Protection contre le MIME-sniffing
  const meta = document.createElement('meta')
  meta.httpEquiv = 'X-Content-Type-Options'
  meta.content = 'nosniff'
  document.head.appendChild(meta)
}

// Fonction pour détecter les attaques potentielles
export function detectSecurityThreats(data: unknown): boolean {
  if (typeof data !== 'string') return false

  const threats = [
    /<script\b[^>]*>([\s\S]*?)<\/script>/gi,  // Scripts malveillants
    /javascript:/gi,                           // Protocole javascript:
    /data:/gi,                                 // Protocole data:
    /on\w+=/gi,                               // Event handlers inline
    /eval\((.*?)\)/gi,                        // Eval()
    /expression\((.*?)\)/gi,                  // Expression CSS
    /vbscript:/gi,                            // VBScript
    /document\.cookie/gi,                     // Accès aux cookies
    /document\.domain/gi,                     // Accès au domaine
    /\[\s*?\$\s*?\w+\s*?\]/gi                // Injection SQL potentielle
  ]

  return threats.some(pattern => pattern.test(data))
}

// Fonction pour logger les tentatives d'attaque
export function logSecurityThreat(threat: string, data: unknown) {
  console.error(`[Security Threat Detected] ${threat}`, {
    timestamp: new Date().toISOString(),
    data,
    url: window.location.href,
    userAgent: navigator.userAgent
  })
  
  // Ici vous pourriez envoyer ces logs à votre serveur
  // ou à un service de monitoring
} 